// src/audio_components/AudioComponent.ts
var AudioComponent = class {
  constructor() {
    this._connectedComponents = [];
  }
  /**Connects the [outputNode] of this [AudioComponent] to the [inputNode] of 
   * the passed in [component]*/
  connect(component) {
    this.outputNode.connect(component.inputNode);
    this._connectedComponents.push(component);
  }
  /**Disconnects the [outputNode] of this [AudioComponent] from the 
   * [inputNode] of the passed in [component]*/
  disconnect(component) {
    let idx = this._connectedComponents.indexOf(component);
    if (idx !== -1) {
      this.outputNode.disconnect(component.inputNode);
      this._connectedComponents.splice(idx);
    }
  }
  /**Disconnects the [outputNode] of this [AudioComponent] from all previously
   * connected [AudioComponent] [inputNode]s.*/
  disconnectAll() {
    for (let component of this._connectedComponents) {
      this.disconnect(component);
    }
  }
  /**the [inputNode] is the target node that other [AudioComponent]
   * instances connect their [outputNode] to. If an [AudioComponent] subclass 
   * can not receive input from another [AudioComponent], this should remain 
   * unaltered in the subclass to generate an error if it is used.*/
  get inputNode() {
    throw "no inputNode getter has been defined";
  }
  /**the outputNode is the source node that connects to other[AudioComponent] 
   * instances' [inputNode]. If an [AudioComponent] subclass can not attach to
   * another [AudioComponent], this should remain unaltered in the subclass to
   * generate an error if it is used.*/
  get outputNode() {
    throw "no outputNode getter has been defined";
  }
};

// src/audio_components/AudioNodeComponent.ts
var AudioNodeComponent = class extends AudioComponent {
  get audioNode() {
    return this._audioNode;
  }
  constructor(audioNode) {
    super();
    this._audioNode = audioNode;
  }
  /**the [inputNode] is the target node that other [AudioComponent]
   * instances connect their [outputNode] to. */
  get inputNode() {
    return this._audioNode;
  }
  /**the outputNode is the source node that connects to other[AudioComponent] 
   * instances' [inputNode].*/
  get outputNode() {
    return this._audioNode;
  }
};

// src/audio_components/DestinationAudioComponent.ts
var DestinationAudioComponent = class extends AudioComponent {
  constructor(context) {
    super();
    this._context = context;
  }
  get inputNode() {
    return this._context.destination;
  }
};

// src/audio_components/MixdownAudioComponent.ts
import { SyncEvent } from "ts-events";
import ysFixWebmDuration from "fix-webm-duration";
var MixdownAudioComponent = class extends AudioComponent {
  constructor(context, mimeCodecSettings) {
    super();
    this._isRecording = false;
    this._mixdownEvent = new SyncEvent();
    this._onDataAvailable = (e) => {
      this._recordingBlobs.push(e.data);
    };
    this._onRecordStop = (e) => {
      if (!this._isRecording) {
        this._mediaRecorder.removeEventListener(
          "dataavailable",
          this._onDataAvailable
        );
        this._mediaRecorder.removeEventListener("stop", this._onRecordStop);
        this._mediaRecorder = null;
        let type = this._canvasSettings != null ? this._mcSettings.videoMimeType : this._mcSettings.audioMimeType;
        let blob = new Blob(this._recordingBlobs, { type });
        this._recordingBlobs = null;
        if (type.includes("webm")) {
          let duration = Math.ceil((this._context.currentTime - this._startTime) * 1e3);
          ysFixWebmDuration(blob, duration, (fixedBlob) => {
            let event = new MixdownEvent();
            event.type = 0 /* recordingReady */;
            event.recording = fixedBlob;
            this._mixdownEvent.post(event);
          }, { logger: false });
        } else {
          let event = new MixdownEvent();
          event.type = 0 /* recordingReady */;
          event.recording = blob;
          this._mixdownEvent.post(event);
        }
      }
    };
    this._onFadeInTimer = () => {
      if (this._fadeInIntervalId == null) {
        return;
      }
      let delta = this._context.currentTime - this._fadeInStartTime;
      if (delta >= this._fadeInTime) {
        this._fadeInIntervalId = null;
      } else {
        let ratio = delta / this._fadeInTime;
        this._drawOverlay(1 - ratio);
        let ms = Math.floor(1e3 / this._canvasSettings.fps);
        this._fadeInIntervalId = window.setTimeout(this._onFadeInTimer, ms);
      }
    };
    this._onFadeOutTimer = () => {
      if (this._fadeOutIntervalId == null) {
        return;
      }
      let canvas = this._canvasSettings.canvas;
      let delta = this._context.currentTime - this._fadeOutStartTime;
      if (delta >= this._fadeOutTime) {
        this._fadeOutIntervalId = null;
        this._drawOverlay(1);
        this._stopRecording();
      } else {
        let ratio = delta / this._fadeOutTime;
        this._drawOverlay(ratio * ratio * ratio * ratio);
        let ms = Math.floor(1e3 / this._canvasSettings.fps);
        this._fadeOutIntervalId = window.setTimeout(
          this._onFadeOutTimer,
          ms
        );
      }
    };
    this._context = context;
    this._mcSettings = mimeCodecSettings;
    this._init();
  }
  get mixdownEvent() {
    return this._mixdownEvent;
  }
  setCanvas(canvas, fps) {
    if (canvas == null) {
      this._canvasSettings = null;
      return;
    }
    this._canvasSettings = new _CanvasMixdownSettings();
    this._canvasSettings.canvas = canvas;
    this._canvasSettings.fps = fps;
    this._context2d = this._canvasSettings.canvas.getContext("2d");
  }
  record(fadeInTime = 0) {
    this._clearFades();
    this._initFadeIn(fadeInTime);
    if (this._mediaRecorder != null) {
      this._mediaRecorder.removeEventListener(
        "dataavailable",
        this._onDataAvailable
      );
      this._mediaRecorder.removeEventListener("stop", this._onRecordStop);
    }
    this._mediaRecorder = this._getMediaRecorder();
    this._mediaRecorder.addEventListener(
      "dataavailable",
      this._onDataAvailable
    );
    this._mediaRecorder.addEventListener("stop", this._onRecordStop);
    this._isRecording = true;
    this._recordingBlobs = [];
    this._startTime = this._context.currentTime;
    this._mediaRecorder.start(1e3);
  }
  stop(fadeOutTime = 0) {
    this._clearFades();
    this._initFadeOut(fadeOutTime);
  }
  _init() {
    this._prepareRecorder();
  }
  _prepareRecorder() {
    this._mediaDestination = this._context.createMediaStreamDestination();
    this._fadeNode = this._context.createGain();
    this._fadeNode.connect(this._mediaDestination);
  }
  _getMediaRecorder() {
    let mediaRecorder;
    if (this._canvasSettings != null) {
      let options = {
        "mimeType": this._mcSettings.videoMimeCodecsString,
        "videoBitsPerSecond": 5e6
      };
      let canvasMediaStream = this._canvasSettings.canvas.captureStream(
        this._canvasSettings.fps
      );
      let audioTracks = this._mediaDestination.stream.getAudioTracks();
      audioTracks.forEach((track) => {
        canvasMediaStream.addTrack(track);
      });
      mediaRecorder = new MediaRecorder(canvasMediaStream, options);
    } else {
      let options = {
        "mimeType": this._mcSettings.audioMimeCodecsString
      };
      mediaRecorder = new MediaRecorder(
        this._mediaDestination.stream,
        options
      );
    }
    return mediaRecorder;
  }
  _stopRecording() {
    this._isRecording = false;
    if (this._mediaRecorder.state === "recording") {
      this._mediaRecorder.stop();
    }
  }
  _initFadeIn(fadeInTime) {
    this._fadeInTime = fadeInTime;
    if (fadeInTime > 0) {
      let ctime = this._context.currentTime;
      this._fadeInStartTime = ctime;
      this._fadeNode.gain.setValueAtTime(0, ctime);
      this._fadeNode.gain.linearRampToValueAtTime(
        1,
        ctime + this._fadeInTime
      );
      this._drawOverlay(1);
      let ms = Math.floor(1e3 / this._canvasSettings.fps);
      this._fadeInIntervalId = window.setTimeout(this._onFadeInTimer, ms);
    } else {
      this._fadeNode.gain.setValueAtTime(1, this._context.currentTime);
    }
  }
  _drawOverlay(opacity) {
    let canvas = this._canvasSettings.canvas;
    this._context2d.fillStyle = `rgba(0,0,0,${opacity})`;
    this._context2d.fillRect(0, 0, canvas.width, canvas.height);
  }
  _initFadeOut(fadeOutTime) {
    this._fadeOutTime = fadeOutTime;
    if (fadeOutTime > 0) {
      let ctime = this._context.currentTime;
      this._fadeOutStartTime = ctime;
      this._fadeNode.gain.setValueAtTime(1, ctime);
      this._fadeNode.gain.linearRampToValueAtTime(
        0,
        ctime + this._fadeOutTime
      );
      let ms = Math.floor(1e3 / this._canvasSettings.fps);
      this._fadeOutIntervalId = window.setTimeout(
        this._onFadeOutTimer,
        ms
      );
    } else {
      this._stopRecording();
    }
  }
  _clearFades() {
    if (this._fadeInIntervalId != null) {
      window.clearTimeout(this._fadeInIntervalId);
      this._fadeInIntervalId = null;
    }
    if (this._fadeOutIntervalId != null) {
      window.clearTimeout(this._fadeOutIntervalId);
      this._fadeOutIntervalId = null;
    }
  }
  get isRecording() {
    return this._isRecording;
  }
  static get isSupported() {
    return !!MediaRecorder;
  }
  get inputNode() {
    return this._fadeNode;
  }
};
var MixdownEvent = class {
};
var MixdownEventType = /* @__PURE__ */ ((MixdownEventType2) => {
  MixdownEventType2[MixdownEventType2["recordingReady"] = 0] = "recordingReady";
  return MixdownEventType2;
})(MixdownEventType || {});
var _CanvasMixdownSettings = class {
};
var MixdownCodecSettings = class {
  constructor() {
    this.videoMimeCodecsString = "video/webm;codecs=vp8,opus";
    this.videoMimeType = "video/webm";
    this.audioMimeCodecsString = "audio/webm; codecs=opus";
    this.audioMimeType = "audio/webm";
  }
};

// src/audio_components/ReverbAudioComponent.ts
var ReverbAudioComponent = class extends AudioComponent {
  constructor(context) {
    super();
    this._context = context;
    this._init();
  }
  setReverbImpulse(buffer) {
    if (buffer == null) {
      this._convolver.buffer = null;
    } else {
      this._convolver.buffer = buffer;
    }
  }
  _init() {
    this._setupAudioGraph();
    this.setReverbImpulse(null);
  }
  _setupAudioGraph() {
    this._preVerbMix = this._context.createGain();
    this._convolver = this._context.createConvolver();
    this._dryMix = this._context.createGain();
    this._wetMix = this._context.createGain();
    this._masterMix = this._context.createGain();
    this._preVerbMix.connect(this._dryMix);
    this._dryMix.connect(this._masterMix);
    this._preVerbMix.connect(this._convolver);
    this._convolver.connect(this._wetMix);
    this._wetMix.connect(this._masterMix);
    this._masterMix.gain.value = 0.8;
    this.wetToDryRatio = 0.3;
  }
  get masterVolume() {
    return this._masterMix.gain.value;
  }
  set masterVolume(value) {
    if (value < 0) {
      value = 0;
    } else if (value > 1)
      value = 1;
    this._masterMix.gain.setValueAtTime(value, 0);
  }
  /**a value from 0 to 1, with 0 meaning no wet signal. The ratio may not
   * exactly match the value provided but may instead be based on it.*/
  get wetToDryRatio() {
    return this._wetMix.gain.value;
  }
  set wetToDryRatio(value) {
    if (value < 0) {
      value = 0;
    } else if (value > 1)
      value = 1;
    this._dryMix.gain.setValueAtTime(1 - value * 0.5, 0);
    this._wetMix.gain.setValueAtTime(value, 0);
  }
  get inputNode() {
    return this._preVerbMix;
  }
  get outputNode() {
    return this._masterMix;
  }
};
export {
  AudioComponent,
  AudioNodeComponent,
  DestinationAudioComponent,
  MixdownAudioComponent,
  MixdownCodecSettings,
  MixdownEvent,
  MixdownEventType,
  ReverbAudioComponent
};
